import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle } from "lucide-react";

/* Razorpay Standard Checkout, reusable button.
 * Props:
 *   flow:     "rfq_deposit" | "advance_topup" | "invoice"
 *   amount:   number  (in major units, e.g. 100.50)
 *   currency: "INR" | "USD"
 *   purpose:  short description shown in Razorpay modal + audit log
 *   metadata: object, passed through to backend notes
 *   projectId / invoiceId / rfqId: pertinent IDs for fulfillment
 *   onSuccess: callback after server-side verification (receives payment record)
 *   className: tailwind classes for the trigger
 *   children:  button label (defaults vary by flow)
 *
 * Loads checkout.js once on first render. LIVE-mode pre-confirm dialog protects
 * against accidental real-money clicks during QA.
 */

let scriptPromise = null;
function loadCheckoutScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error("Razorpay script failed"));
    document.body.appendChild(s);
  });
  return scriptPromise;
}

export default function RazorpayCheckout({
  flow,
  amount,
  currency = "INR",
  purpose,
  metadata = {},
  projectId,
  invoiceId,
  rfqId,
  customerName,
  customerEmail,
  customerContact,
  onSuccess,
  className = "btn-primary !py-2.5 !px-5 !text-[11px]",
  children,
  testId,
  disabled,
}) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get("/payments/config").then((r) => setConfig(r.data)).catch(() => {});
    loadCheckoutScript().catch(() => {});
  }, []);

  const proceed = async () => {
    if (!config?.configured) {
      toast.error("Payments not configured");
      return;
    }
    if (!amount || amount <= 0) {
      toast.error("Invalid amount");
      return;
    }
    setBusy(true);
    try {
      await loadCheckoutScript();
      const r = await api.post("/payments/order", {
        flow, amount: Number(amount), currency, purpose,
        metadata, project_id: projectId, invoice_id: invoiceId, rfq_id: rfqId,
      });
      const order = r.data;
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount_minor,
        currency: order.currency,
        order_id: order.order_id,
        name: "Fortune Sourcings",
        description: purpose,
        image: "/icon-512.png",
        theme: { color: "#12283F" },
        prefill: {
          name: customerName || "",
          email: customerEmail || "",
          contact: customerContact || "",
        },
        notes: { flow, purpose },
        handler: async (resp) => {
          try {
            const v = await api.post("/payments/verify", resp);
            toast.success("Payment successful");
            onSuccess?.(v.data.payment);
          } catch (e) {
            toast.error("Verification failed: " + (e.response?.data?.detail || e.message));
          }
        },
        modal: {
          ondismiss: () => setBusy(false),
        },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error("Payment failed: " + (resp.error?.description || "unknown"));
        setBusy(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment");
      setBusy(false);
    }
  };

  const click = () => {
    if (config?.mode === "live") setConfirming(true);
    else proceed();
  };

  return (
    <>
      <button
        data-testid={testId || `pay-${flow}-btn`}
        onClick={click}
        disabled={busy || disabled}
        className={`${className} disabled:opacity-50`}
      >
        {busy ? "Opening checkout…" : (children ||
          (flow === "rfq_deposit" ? "Pay engagement retainer"
           : flow === "advance_topup" ? "Top up advance"
           : "Pay invoice"))}
      </button>

      {confirming && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" data-testid="pay-confirm-modal">
          <div className="bg-white max-w-md w-full border-2 border-[#DC2626]">
            <div className="bg-[#DC2626] text-white px-5 py-3 flex items-center gap-2">
              <AlertTriangle size={18}/>
              <div className="font-serif text-lg">LIVE Payment Confirmation</div>
            </div>
            <div className="p-6">
              <p className="text-sm text-[#12283F] leading-relaxed">
                You are about to initiate a <strong>real money transaction</strong> for:
              </p>
              <div className="my-4 p-4 bg-[#F8FAFC] border border-[#E2E8F0]">
                <div className="eyebrow text-[#6B7280] text-[9px]">Amount</div>
                <div className="font-serif text-2xl text-[#12283F]">
                  {currency} {Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
                <div className="eyebrow text-[#6B7280] text-[9px] mt-3">Purpose</div>
                <div className="text-sm text-[#12283F]">{purpose}</div>
              </div>
              <p className="text-xs text-[#6B7280] mb-4 flex items-start gap-2">
                <ShieldCheck size={14} className="text-emerald-700 mt-0.5 shrink-0"/>
                Razorpay {config?.mode?.toUpperCase()} mode · {config?.key_id?.slice(0, 14)}…
              </p>
              <div className="flex gap-2">
                <button data-testid="pay-confirm-cancel" onClick={() => setConfirming(false)}
                  className="btn-outline !py-2 !px-4 !text-[11px] flex-1">Cancel</button>
                <button data-testid="pay-confirm-proceed" onClick={() => { setConfirming(false); proceed(); }}
                  className="btn-primary !py-2 !px-4 !text-[11px] flex-1">Proceed to pay</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
